import { FieldType, KeyValuePair, ViewModelBase } from "@shoothill/core";
import { action, observable, runInAction, computed } from "mobx";

import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { IssueItemViewModel, IssueModel } from ".";
import { GenericId } from "Globals/Models";
import { GenericIdWithDisplayName, GenericIdWithDisplayNameDTO } from "Globals/Models/GenericIdWithDisplayName";

export class IssueViewModel extends ViewModelBase<IssueModel> {
    public server: ServerViewModel = new ServerViewModel();

    private rfiIds: string[];
    private projectId: string | null;
    public issueItems = observable<IssueItemViewModel>([]);
    private parentCloseAction: (refreshPage: boolean) => void;

    @observable
    public approvalProjectDirectoryPersonUserOptions: GenericIdWithDisplayName[] = [];

    @action
    public populateApprovalProjectDirectoryPersonUserOptions = (dto: GenericIdWithDisplayNameDTO[]): void => {
        dto.forEach((u) => {
            let model = new GenericIdWithDisplayName();
            model.fromDto(u);
            this.approvalProjectDirectoryPersonUserOptions.push(model);
        });
    };

    @computed
    public get getProjectDirectoryPersonUserOptions(): KeyValuePair<any>[] {
        return this.approvalProjectDirectoryPersonUserOptions.map((item: GenericIdWithDisplayName) => {
            return { key: item.displayName, value: item.id };
        });
    }

    @action
    public setSelectedToProjectDirectoryPersonUsers = (val: string[]) => {
        this.model.selectedToProjectDirectoryPersonUsers = val;
    };

    @action
    public setSelectedCCProjectDirectoryPersonUsers = (val: string[]) => {
        this.model.selectedCCProjectDirectoryPersonUsers = val;
    };

    constructor(rfiIds: string[], projectId: string | null, closeAction: (refreshPage: boolean) => void) {
        super(new IssueModel(), false);

        this.rfiIds = rfiIds;
        this.projectId = projectId;
        this.parentCloseAction = closeAction;

        this.setDecorators(IssueModel);

        // Load issue data.
        this.apiLoadIssueDataAsync(projectId);
    }

    @computed
    public get title(): string {
        return "Issue RFI";
    }

    // #region Actions

    @action
    public cancel = () => this.parentCloseAction(false);

    // #endregion Actions

    // #region Api Actions

    @action
    public apiLoadIssueDataAsync = async (projectId: string | null): Promise<void> => {
        this.setIsLoading(true);
        const dto: GenericId = { id: projectId! };
        await this.server.query<any>(
            () => this.Post(AppUrls.Server.Projects.ProjectTrackers.RFI.IssueRelatedByRFIIds, dto),
            (result) => {
                runInAction(() => {
                    this.populateApprovalProjectDirectoryPersonUserOptions(result.projectDirectoryPersons);
                });
            },
            "Error whilst loading RFI data",
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
        this.setIsLoading(false);
    };

    @action
    public apiIssueAsync = async (): Promise<void> => {
        this.setIsLoading(true);

        if (await this.isModelValid()) {
            let model = this.model.toDto();
            model.rfiIds = this.rfiIds;
            model.projectId = this.projectId;
            await this.server.command<any>(
                () => this.Post(AppUrls.Server.Projects.ProjectTrackers.RFI.IssueRFIs, model),
                (result) => this.parentCloseAction(true),
                this.isModelValid,
                "Error whilst issuing RFIs",
            );

            if (this.server.HaveValidationMessage) {
                this.setSnackMessage(this.server.ValidationMessage);
                this.setSnackType(this.SNACKERROR);
                this.setSnackbarState(true);
            }
        }

        this.setIsLoading(false);
    };

    // #endregion Api Actions

    // #region Snack Bar

    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @observable
    public snackType = "";

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    // #endregion Snack Bar

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<IssueModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boliderplate
}
